import { type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import type {
  ProvisionStatus,
  ProvisionStep,
  PendingMigration,
} from "skedyul-core/features/deployments/resources/types";

// Infer types from tRPC router
export type ProvisionDetail = NonNullable<
  inferRouterOutputs<AppRouter>["apps"]["getProvision"]
>;

// Log entry for provision logs display
export type ProvisionLogEntry = {
  id: string;
  timestamp: string;
  message: string;
  level: "info" | "warn" | "error";
  type?: "step" | "approval_required";
  stepStatus?: ProvisionStep["status"];
};

// Realtime update payload for provision updates
type ProvisionUpdate = {
  provisionId: string;
  status?: ProvisionStatus;
  currentStep?: string | null;
  steps?: ProvisionStep[];
  errorMessage?: string | null;
  completedAt?: string | null;
  pendingMigration?: PendingMigration | null;
};

export interface ProvisionsState {
  // Single provision detail (for sidebar)
  provision: {
    isLoading: boolean;
    data: ProvisionDetail | null;
    error: string | null;
  };
  // Pending migration requiring approval
  pendingMigration: PendingMigration | null;
  // Provision logs for display
  logs: ProvisionLogEntry[];
}

const initialState: ProvisionsState = {
  provision: {
    isLoading: false,
    data: null,
    error: null,
  },
  pendingMigration: null,
  logs: [],
};

// Async thunks
export const getProvision = createAsyncThunk(
  "provisions/getProvision",
  async (payload: { provisionId: string }) => {
    return await trpc.apps.getProvision.query(payload);
  },
);

export const provisionsSlice = createSlice({
  name: "provisions",
  initialState,
  reducers: {
    // Clear the current provision (useful when navigating away)
    clearProvision: (state) => {
      state.provision.data = null;
      state.provision.error = null;
      state.provision.isLoading = false;
      state.pendingMigration = null;
      state.logs = [];
    },
    // Set provision data directly (useful for initial load from provider)
    setProvision: (
      state,
      action: PayloadAction<{
        provision: ProvisionDetail | null;
      }>,
    ) => {
      state.provision.data = action.payload.provision;
      state.provision.isLoading = false;
      state.provision.error = null;
      // Convert steps to logs when setting provision
      if (action.payload.provision?.steps) {
        state.logs = stepsToLogs(action.payload.provision.steps);
      }
      applyPendingMigrationFromProvision(state, action.payload.provision);
    },
    // Set pending migration (for approval flow)
    setPendingMigration: (
      state,
      action: PayloadAction<PendingMigration | null>,
    ) => {
      state.pendingMigration = action.payload;
    },
    // Clear pending migration (after approval/denial)
    clearPendingMigration: (state) => {
      state.pendingMigration = null;
    },
    // Add a log entry
    addLog: (state, action: PayloadAction<ProvisionLogEntry>) => {
      state.logs.push(action.payload);
    },
    // Set all logs
    setLogs: (state, action: PayloadAction<ProvisionLogEntry[]>) => {
      state.logs = action.payload;
    },
    // Apply realtime provision updates from Pusher
    applyProvisionUpdate: (state, action: PayloadAction<ProvisionUpdate>) => {
      const {
        provisionId,
        status,
        currentStep,
        steps,
        errorMessage,
        completedAt,
        pendingMigration,
      } = action.payload;

      const matchesLoadedProvision = state.provision.data?.id === provisionId;

      // Stash approval payload from Pusher if provision data has not loaded yet.
      // Drop it when a different provision is already loaded.
      if (!matchesLoadedProvision) {
        if (pendingMigration !== undefined && state.provision.data == null) {
          state.pendingMigration = pendingMigration;
        }
        return;
      }

      // Update fields if provided
      if (status !== undefined && state.provision.data) {
        state.provision.data = {
          ...state.provision.data,
          status,
        };
      }

      if (currentStep !== undefined && state.provision.data) {
        state.provision.data = {
          ...state.provision.data,
          currentStep,
        };
      }

      if (steps !== undefined && state.provision.data) {
        state.provision.data = {
          ...state.provision.data,
          steps,
        };
        // Merge step-derived logs with streamed entries instead of replacing
        const stepLogs = stepsToLogs(steps);
        const logMap = new Map<string, ProvisionLogEntry>();
        for (const log of state.logs) {
          logMap.set(`${log.timestamp}-${log.message}`, log);
        }
        for (const log of stepLogs) {
          const key = `${log.timestamp}-${log.message}`;
          if (!logMap.has(key)) {
            logMap.set(key, log);
          }
        }
        state.logs = Array.from(logMap.values());
      }

      if (errorMessage !== undefined && state.provision.data) {
        state.provision.data = {
          ...state.provision.data,
          errorMessage,
        };
      }

      if (completedAt !== undefined && state.provision.data) {
        state.provision.data = {
          ...state.provision.data,
          completedAt,
        };
      }

      // Handle pending migration updates
      if (pendingMigration !== undefined) {
        state.pendingMigration = pendingMigration;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getProvision
      .addCase(getProvision.pending, (state) => {
        state.provision.isLoading = true;
        state.provision.error = null;
      })
      .addCase(getProvision.fulfilled, (state, action) => {
        state.provision.isLoading = false;
        state.provision.data = action.payload;
        // Merge step-derived logs with streamed entries instead of replacing
        if (action.payload?.steps) {
          const stepLogs = stepsToLogs(action.payload.steps);
          const logMap = new Map<string, ProvisionLogEntry>();
          for (const log of state.logs) {
            logMap.set(`${log.timestamp}-${log.message}`, log);
          }
          for (const log of stepLogs) {
            const key = `${log.timestamp}-${log.message}`;
            if (!logMap.has(key)) {
              logMap.set(key, log);
            }
          }
          state.logs = Array.from(logMap.values());
        }
        applyPendingMigrationFromProvision(state, action.payload);
      })
      .addCase(getProvision.rejected, (state, action) => {
        state.provision.isLoading = false;
        state.provision.error =
          action.error.message ?? "Failed to load provision";
      });
  },
});

function applyPendingMigrationFromProvision(
  state: ProvisionsState,
  provision: ProvisionDetail | null | undefined,
): void {
  if (!provision) {
    state.pendingMigration = null;
    return;
  }

  if (provision.pendingMigration) {
    state.pendingMigration = provision.pendingMigration as PendingMigration;
    return;
  }

  // Keep a Pusher-stashed approval while still awaiting; clear otherwise.
  if (provision.status !== "AWAITING_APPROVAL") {
    state.pendingMigration = null;
  }
}

// Helper function to convert provision steps to log entries
function stepsToLogs(steps: ProvisionStep[]): ProvisionLogEntry[] {
  return steps.map((step, index) => ({
    id: `step-${index}`,
    timestamp: step.startedAt || new Date().toISOString(),
    message: step.name,
    level:
      step.status === "failed"
        ? "error"
        : step.status === "awaiting_approval"
          ? "warn"
          : "info",
    type: "step" as const,
    stepStatus: step.status,
  }));
}

// Export actions
export const {
  clearProvision,
  setProvision,
  setPendingMigration,
  clearPendingMigration,
  addLog,
  setLogs,
  applyProvisionUpdate,
} = provisionsSlice.actions;

export default provisionsSlice.reducer;
